import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, TextField, FormControl, InputLabel, Select,
  MenuItem, IconButton, Tooltip, CircularProgress, Chip, Collapse,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Pagination,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import api from '../services/api';

const STATUS_COLOR = {
  ok:    { bg: '#1c3a2a', color: '#68d391', icon: '✅' },
  warn:  { bg: '#3a2e10', color: '#f6c90e', icon: '⚠️' },
  error: { bg: '#3a1c1c', color: '#fc8181', icon: '❌' },
  info:  { bg: '#1a2a3a', color: '#90cdf4', icon: 'ℹ️' },
  skip:  { bg: '#2a2a2a', color: '#a0aec0', icon: '⏭' },
};

function StepRow({ step, depth = 0 }) {
  const [open, setOpen] = useState(depth < 1);
  const s = STATUS_COLOR[step.status] || STATUS_COLOR.info;
  const hasChildren = step.children && step.children.length > 0;

  return (
    <>
      <TableRow
        sx={{
          background: depth === 0 ? '#1a1d2e' : depth === 1 ? '#13161f' : '#0f1117',
          '&:hover': { background: depth === 0 ? '#1e2235' : '#161920' },
          cursor: hasChildren ? 'pointer' : 'default',
        }}
        onClick={() => hasChildren && setOpen(o => !o)}
      >
        <TableCell sx={{ pl: 1 + depth * 3, py: 0.5, borderBottom: '1px solid #1e2235' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {hasChildren && (
              <IconButton size="small" sx={{ p: 0, color: '#4a5568' }}>
                {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            )}
            {!hasChildren && <Box sx={{ width: 20 }} />}
            <Box
              sx={{
                width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                background: s.color,
              }}
            />
            <Typography
              variant="body2"
              sx={{ fontSize: depth === 0 ? '0.85rem' : '0.78rem', color: depth === 0 ? '#e2e8f0' : '#a0aec0' }}
            >
              {step.message}
            </Typography>
          </Box>
        </TableCell>
        <TableCell sx={{ py: 0.5, borderBottom: '1px solid #1e2235', whiteSpace: 'nowrap' }}>
          {step.material_name && (
            <Chip label={step.material_name} size="small" sx={{ fontSize: '0.7rem', height: 20 }} />
          )}
        </TableCell>
        <TableCell sx={{ py: 0.5, borderBottom: '1px solid #1e2235', fontSize: '0.7rem', color: '#718096', whiteSpace: 'nowrap' }}>
          {step.warehouse && (
            <Chip label={step.warehouse} size="small" variant="outlined"
              sx={{ fontSize: '0.68rem', height: 18, borderColor: '#2d3748', color: '#90cdf4' }} />
          )}
        </TableCell>
        <TableCell sx={{ py: 0.5, borderBottom: '1px solid #1e2235', fontSize: '0.75rem', color: '#718096', whiteSpace: 'nowrap' }}>
          {step.quantity_have != null && (
            <span>є: <b style={{ color: '#e2e8f0' }}>{Number(step.quantity_have).toFixed(2)}</b> </span>
          )}
          {step.quantity_need != null && (
            <span>потр: <b style={{ color: '#e2e8f0' }}>{Number(step.quantity_need).toFixed(2)}</b> </span>
          )}
          {step.quantity_delta != null && (
            <span style={{ color: step.quantity_delta < 0 ? '#fc8181' : '#68d391' }}>
              ({step.quantity_delta > 0 ? '+' : ''}{Number(step.quantity_delta).toFixed(2)})
            </span>
          )}
          {step.unit && <span style={{ marginLeft: 2, color: '#4a5568' }}>{step.unit}</span>}
        </TableCell>
        <TableCell sx={{ py: 0.5, borderBottom: '1px solid #1e2235', fontSize: '0.7rem', color: '#4a5568', whiteSpace: 'nowrap' }}>
          {depth === 0 && new Date(step.created_at).toLocaleString('uk-UA')}
        </TableCell>
        <TableCell sx={{ py: 0.5, borderBottom: '1px solid #1e2235', fontSize: '0.7rem', color: '#4a5568' }}>
          {depth === 0 && (step.staff_name || 'Система')}
        </TableCell>
      </TableRow>
      {hasChildren && open && step.children.map(child => (
        <StepRow key={child.id} step={child} depth={depth + 1} />
      ))}
    </>
  );
}

const PAGE_SIZE = 30;

export default function ProcessLogPage({ currentUser }) {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [staffList, setStaffList] = useState([]);

  const [filters, setFilters] = useState({
    entity_type: '', staff: '', date_from: '', date_to: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (filters.entity_type) params.entity_type = filters.entity_type;
      if (filters.staff) params.staff = filters.staff;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const res = await api.get('/process-log/', { params });
      const data = res.data;
      if (Array.isArray(data)) { setLogs(data); setTotal(data.length); }
      else { setLogs(data.results || []); setTotal(data.count || 0); }
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/staff/').then(r => setStaffList(Array.isArray(r.data) ? r.data : r.data.results || []));
  }, []);

  const setFilter = (k, v) => { setFilters(f => ({ ...f, [k]: v })); setPage(1); };

  const entityTypes = [
    { v: 'ProductionOrder', l: 'Вир. замовлення' },
    { v: 'CustomerOrder', l: 'Замовлення клієнта' },
    { v: 'PurchaseOrder', l: 'Закупівля' },
    { v: 'StockArrival', l: 'Прихід на склад' },
    { v: 'MixingBatch', l: 'Заміс' },
  ];

  return (
    <Box sx={{ p: 2, height: 'calc(100vh - 112px)', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#0f1117', color: '#e2e8f0' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#90cdf4' }}>
          📋 Журнал процесів
        </Typography>
        <Typography variant="body2" sx={{ color: '#4a5568', fontSize: '0.78rem' }}>
          Перевірки залишків, резервування, списання — детальна стрічка кожної операції
        </Typography>

        <FormControl size="small" sx={{ minWidth: 180, '& .MuiOutlinedInput-root': { color: '#e2e8f0' }, '& .MuiInputLabel-root': { color: '#718096' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2d3748' } }}>
          <InputLabel>Тип операції</InputLabel>
          <Select value={filters.entity_type} label="Тип операції" onChange={e => setFilter('entity_type', e.target.value)}>
            <MenuItem value="">Всі</MenuItem>
            {entityTypes.map(e => <MenuItem key={e.v} value={e.v}>{e.l}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160, '& .MuiOutlinedInput-root': { color: '#e2e8f0' }, '& .MuiInputLabel-root': { color: '#718096' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2d3748' } }}>
          <InputLabel>Користувач</InputLabel>
          <Select value={filters.staff} label="Користувач" onChange={e => setFilter('staff', e.target.value)}>
            <MenuItem value="">Всі</MenuItem>
            {staffList.map(s => <MenuItem key={s.id} value={s.id}>{s.full_name || s.username}</MenuItem>)}
          </Select>
        </FormControl>

        <TextField size="small" type="date" label="Від" value={filters.date_from}
          onChange={e => setFilter('date_from', e.target.value)} InputLabelProps={{ shrink: true }}
          sx={{ '& .MuiOutlinedInput-root': { color: '#e2e8f0' }, '& .MuiInputLabel-root': { color: '#718096' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2d3748' } }} />
        <TextField size="small" type="date" label="До" value={filters.date_to}
          onChange={e => setFilter('date_to', e.target.value)} InputLabelProps={{ shrink: true }}
          sx={{ '& .MuiOutlinedInput-root': { color: '#e2e8f0' }, '& .MuiInputLabel-root': { color: '#718096' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2d3748' } }} />

        <Tooltip title="Оновити">
          <IconButton onClick={load} size="small" sx={{ color: '#718096' }}><RefreshIcon /></IconButton>
        </Tooltip>
        <Typography variant="body2" sx={{ ml: 'auto', color: '#4a5568' }}>{total} операцій</Typography>
      </Box>

      <TableContainer component={Paper} sx={{ flex: 1, overflow: 'auto', background: '#13161f', border: '1px solid #2d3748' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: '#1a1d2e' }}>
                <TableCell sx={{ color: '#718096', fontSize: '0.75rem', fontWeight: 600 }}>Операція / Крок</TableCell>
                <TableCell sx={{ color: '#718096', fontSize: '0.75rem', fontWeight: 600, width: 150 }}>Матеріал</TableCell>
                <TableCell sx={{ color: '#718096', fontSize: '0.75rem', fontWeight: 600, width: 140 }}>Склад</TableCell>
                <TableCell sx={{ color: '#718096', fontSize: '0.75rem', fontWeight: 600, width: 220 }}>Кількість</TableCell>
                <TableCell sx={{ color: '#718096', fontSize: '0.75rem', fontWeight: 600, width: 150 }}>Час</TableCell>
                <TableCell sx={{ color: '#718096', fontSize: '0.75rem', fontWeight: 600, width: 130 }}>Користувач</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6, color: '#4a5568' }}>
                    Операцій ще немає. Виконайте будь-яку дію в системі (запуск виробництва, перевірка складу тощо).
                  </TableCell>
                </TableRow>
              )}
              {logs.map(log => <StepRow key={log.id} step={log} depth={0} />)}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {total > PAGE_SIZE && (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
          <Pagination count={Math.ceil(total / PAGE_SIZE)} page={page} onChange={(_, v) => setPage(v)}
            sx={{ '& .MuiPaginationItem-root': { color: '#718096' } }} />
        </Box>
      )}
    </Box>
  );
}
